import { useState, useRef, useCallback, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'

// BootstrapVue-style hover tooltip (dark bubble). Teen placements:
//   - 'right'  (default) — /admin/users User Name jaisa (right me, vertically center)
//   - 'bottom'           — current-bets Browser "Detail" jaisa (neeche, horizontally center)
//   - 'top'              — More modal ke phone/city icons jaisa (v-b-tooltip.hover.top;
//                          upar, horizontally center, arrow neeche ki taraf)
// Class + style reference ke exact: <div class="tooltip b-tooltip bs-tooltip-<pl>"
// x-placement="<pl>" style="position:absolute; top:0; left:0; transform:translate3d(x,y,0)">.
// Visible isliye kyunki app.css me `.tooltip.b-tooltip { opacity:.9 }` hai. Body me
// portal (table overflow se clip na ho). Popper jaisa translate3d se position.
let tipCounter = 2000

export default function Tooltip({ text, children, className, placement = 'right' }) {
  const [rect, setRect] = useState(null)
  const [tipSize, setTipSize] = useState({ w: 0, h: 0 })
  const triggerRef = useRef(null)
  const tipRef = useRef(null)
  const idRef = useRef(null)
  if (idRef.current === null) idRef.current = `__bv_tooltip_${tipCounter++}__`

  const show = useCallback(() => {
    if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect())
  }, [])
  const hide = useCallback(() => setRect(null), [])

  // tooltip render hone ke baad uska size maapo taaki center kar saken
  useLayoutEffect(() => {
    if (rect && tipRef.current) setTipSize({ w: tipRef.current.offsetWidth, h: tipRef.current.offsetHeight })
  }, [rect, text])

  const gap = 6
  const isBottom = placement === 'bottom'
  const isTop = placement === 'top'
  let x = 0
  let y = 0
  if (rect) {
    if (isBottom) {
      x = Math.round(rect.left + window.scrollX + rect.width / 2 - tipSize.w / 2)
      y = Math.round(rect.bottom + window.scrollY + gap)
    } else if (isTop) {
      // Mirror of 'bottom': centred over the trigger, sitting `gap` above it.
      // app.css gives .bs-tooltip-top `padding: .4rem 0` and pins .arrow to
      // bottom: 0, so the arrow lives in that padding and points down.
      x = Math.round(rect.left + window.scrollX + rect.width / 2 - tipSize.w / 2)
      y = Math.round(rect.top + window.scrollY - tipSize.h - gap)
    } else {
      x = Math.round(rect.right + window.scrollX + gap)
      y = Math.round(rect.top + window.scrollY + rect.height / 2 - tipSize.h / 2)
    }
  }
  // arrow: top/bottom -> horizontally center; right -> vertically center (left edge)
  const arrowStyle = (isBottom || isTop)
    ? { left: '50%', transform: 'translateX(-50%)' }
    : { top: '50%', transform: 'translateY(-50%)' }

  return (
    <>
      <span
        ref={triggerRef}
        className={className}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={rect ? idRef.current : undefined}
        style={{ display: 'inline-block' }}
      >
        {children}
      </span>
      {rect && text && createPortal(
        <div
          ref={tipRef}
          id={idRef.current}
          role="tooltip"
          tabIndex={-1}
          x-placement={placement}
          className={`tooltip b-tooltip bs-tooltip-${placement}`}
          style={{ position: 'absolute', top: 0, left: 0, transform: `translate3d(${x}px, ${y}px, 0px)`, willChange: 'transform' }}
        >
          <div className="arrow" style={arrowStyle}></div>
          <div className="tooltip-inner">{text}</div>
        </div>,
        document.body,
      )}
    </>
  )
}
